import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-pro";
import { useGetPromotionProductsMutation } from "../../../../redux/reducer/features/Product/promotionSlice";
import { round2 } from "../../../../services/utilities/utility";
import WarningIcon from "@mui/icons-material/Warning";

interface PromotionProduct {
  id: string;
  mfrName: string | null;
  modelName: string | null;
  title: string | null;
  model: string | null;
  partNumber: string | null;
  cost: number;
  price: number;
  promotionPrice: number;
  stock: number;
}

interface PromotionProductsDialogProps {
  open: boolean;
  onClose: () => void;
  promotionId: string | null;
  promotionName: string;
}

const formatCurrency = (value: number | null | undefined): string =>
  `$${round2(value ?? 0).toFixed(2)}`;

const columns: GridColDef[] = [
  {
    field: "mfrName",
    headerName: "Manufacturer",
    minWidth: 150,
    flex: 1,
    sortable: true,
    valueGetter: (params) => params.row.mfrName ?? "N/A",
    renderCell: (params) => (
      <Tooltip title={params.value} arrow>
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "title",
    headerName: "Title",
    minWidth: 200,
    flex: 1.5,
    sortable: true,
    valueGetter: (params) => params.row.title ?? "N/A",
    renderCell: (params) => (
      <Tooltip title={params.value} arrow>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "mpn",
    headerName: "MPN",
    minWidth: 130,
    sortable: true,
    valueGetter: (params) => params.row.mpn ?? "N/A",
    renderCell: (params) => (
      <Typography variant="body2" noWrap>
        {params.value}
      </Typography>
    ),
  },
  {
    field: "cost",
    headerName: "Cost",
    width: 100,
    type: "number",
    sortable: true,
    renderCell: (params) => (
      <Typography variant="body2">{formatCurrency(params.row.cost)}</Typography>
    ),
  },
  {
    field: "price",
    headerName: "MSRP",
    width: 100,
    type: "number",
    sortable: true,
    renderCell: (params) => (
      <Typography variant="body2">
        {formatCurrency(params.row.price)}
      </Typography>
    ),
  },
  {
    field: "promotionPrice",
    headerName: "Promo Price",
    width: 120,
    type: "number",
    sortable: true,
    renderCell: (params) => {
      const price = params.row.promotionPrice;
      const formattedPrice = formatCurrency(price);
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            {formattedPrice}
          </Typography>
          {price <= 0 && (
            <Tooltip title="Sale price is zero or less than zero. This promotion won't be activated or published online.">
              <WarningIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    align: "center",
    headerAlign: "center",
    sortable: false,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        color={
          params.value === "Active"
            ? "success"
            : params.value === "Expired"
              ? "error"
              : "warning"
        }
      />
    ),
  },
  {
    field: "stock",
    headerName: "Stock",
    width: 90,
    align: "center",
    headerAlign: "center",
    type: "number",
    sortable: true,
    renderCell: (params) => {
      const stock = params.row.stock ?? 0;
      return (
        <Chip
          label={stock}
          size="small"
          variant="outlined"
          color={stock <= 0 ? "error" : "default"}
        />
      );
    },
  },
];

export default function PromotionProductsDialog({
  open,
  onClose,
  promotionId,
  promotionName,
}: PromotionProductsDialogProps) {
  const [getPromotionProducts, getPromotionProductsResult] =
    useGetPromotionProductsMutation();
  const [products, setProducts] = React.useState<PromotionProduct[]>([]);

  React.useEffect(() => {
    if (open && promotionId) {
      getPromotionProducts({ promotionId });
    }
  }, [open, promotionId, getPromotionProducts]);

  React.useEffect(() => {
    if (
      getPromotionProductsResult.isSuccess &&
      getPromotionProductsResult.data
    ) {
      setProducts(getPromotionProductsResult.data.entity?.products ?? []);
    }
  }, [getPromotionProductsResult]);

  const CustomToolbar = React.useCallback(
    () => (
      <GridToolbarContainer>
        <Box sx={{ flexGrow: 1 }} />
        <GridToolbarExport
          variant="outlined"
          size="small"
          color="primary"
          csvOptions={{ fileName: `${promotionName || "promotion"}_products` }}
          printOptions={{ hideToolbar: true, hideFooter: true }}
        />
      </GridToolbarContainer>
    ),
    [promotionName],
  );

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Products on Promotion: {promotionName}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Paper variant="outlined" sx={{ borderRadius: 0, border: 0 }}>
          <Box sx={{ height: "60vh" }}>
            <DataGridPro
              rows={products}
              columns={columns}
              loading={getPromotionProductsResult.isLoading}
              disableSelectionOnClick
              disableColumnMenu
              pagination
              rowsPerPageOptions={[50, 100]}
              initialState={{
                pagination: { pageSize: 50 },
              }}
              components={{ Toolbar: CustomToolbar }}
            />
          </Box>

          {/* ── Footer ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {products.length} product(s) match this promotion
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
